import React, { useState, useEffect } from "react"
import { format, addDays, startOfTomorrow } from "date-fns"
import { parseApiTimestamp, toDateOnlyString } from "@/lib/datetime"
import { CalendarIcon } from "lucide-react"
import { DateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"

type PauseMode = "indefinite" | "dateRange"

interface PostponeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  onConfirm: (pauseStart: string, pauseEnd: string, reason?: string) => Promise<void>
  onPauseIndefinitely?: () => Promise<void>
  isLoading?: boolean
  currentPauseStart?: string | null
  currentPauseEnd?: string | null
  showIndefiniteOption?: boolean
  isPaused?: boolean
}

export function PostponeDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onPauseIndefinitely,
  isLoading = false,
  currentPauseStart,
  currentPauseEnd,
  showIndefiniteOption = false,
  isPaused = false,
}: PostponeDialogProps) {
  const [pauseMode, setPauseMode] = useState<PauseMode>("dateRange")
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    if (currentPauseStart && currentPauseEnd) {
      return {
        from: parseApiTimestamp(currentPauseStart) ?? undefined,
        to: parseApiTimestamp(currentPauseEnd) ?? undefined,
      }
    }
    return undefined
  })
  const [reason, setReason] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setPauseMode("dateRange")
      if (currentPauseStart && currentPauseEnd) {
        setDateRange({
          from: parseApiTimestamp(currentPauseStart) ?? undefined,
          to: parseApiTimestamp(currentPauseEnd) ?? undefined,
        })
      } else {
        setDateRange(undefined)
      }
      setReason("")
    }
  }, [open, currentPauseStart, currentPauseEnd])

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      if (pauseMode === "indefinite" && onPauseIndefinitely) {
        await onPauseIndefinitely()
      } else if (pauseMode === "dateRange" && dateRange?.from && dateRange?.to) {
        await onConfirm(
          toDateOnlyString(dateRange.from),
          toDateOnlyString(dateRange.to),
          reason || undefined
        )
      }
      onOpenChange(false)
    } catch (error) {
      console.error("Failed to pause campaign:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleQuickSelect = (days: number) => {
    const tomorrow = startOfTomorrow()
    setDateRange({
      from: tomorrow,
      to: addDays(tomorrow, days - 1),
    })
  }

  const isValid = pauseMode === "indefinite"
    ? !!onPauseIndefinitely
    : (dateRange?.from && dateRange?.to)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Pause mode selection */}
          {showIndefiniteOption && onPauseIndefinitely && (
            <div className="space-y-3">
              <Label>Pause type</Label>
              <RadioGroup
                value={pauseMode}
                onValueChange={(value) => setPauseMode(value as PauseMode)}
                className="flex flex-col gap-3"
              >
                <div className="flex items-start space-x-3">
                  <RadioGroupItem value="indefinite" id="indefinite" className="mt-1" />
                  <div className="grid gap-1">
                    <Label htmlFor="indefinite" className="font-medium cursor-pointer">
                      Pause indefinitely
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Stop all calls until manually resumed
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <RadioGroupItem value="dateRange" id="dateRange" className="mt-1" />
                  <div className="grid gap-1">
                    <Label htmlFor="dateRange" className="font-medium cursor-pointer">
                      Pause for specific dates
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Pause calls during a date range, then auto-resume
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Date range options - only show when dateRange mode is selected */}
          {pauseMode === "dateRange" && (
            <>
              {/* Quick select buttons */}
              <div className="space-y-2">
                <Label>Quick select</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickSelect(7)}
                  >
                    1 week
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickSelect(14)}
                  >
                    2 weeks
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickSelect(30)}
                  >
                    1 month
                  </Button>
                </div>
              </div>

              {/* Date range picker */}
              <div className="space-y-2">
                <Label>Pause period</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal whitespace-normal h-auto min-h-10",
                        !dateRange && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      {dateRange?.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, "MMM d, yyyy")} -{" "}
                            {format(dateRange.to, "MMM d, yyyy")}
                          </>
                        ) : (
                          format(dateRange.from, "MMM d, yyyy")
                        )
                      ) : (
                        "Select date range"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] p-0" align="start">
                    {/* One month on phones, two on wider screens */}
                    <div className="md:hidden">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={dateRange?.from}
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={1}
                        disabled={{ before: new Date() }}
                      />
                    </div>
                    <div className="hidden md:block">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={dateRange?.from}
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={2}
                        disabled={{ before: new Date() }}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Reason input */}
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Input
                  id="reason"
                  placeholder="e.g., Holiday period, Patient request"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting || isLoading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || isSubmitting || isLoading}
            className="w-full sm:w-auto"
          >
            {isSubmitting || isLoading
              ? "Saving..."
              : pauseMode === "indefinite"
                ? "Pause Campaign"
                : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PostponeDialog
